import React, { useState, useEffect } from 'react';
import {
  Select,
  Skeleton,
  Spin,
  Alert,
  Modal,
  Button,
  notification
} from 'antd';
import { RobotOutlined, CheckOutlined, WarningOutlined } from '@ant-design/icons';
import { Workspace } from '../../types';
import { useChatStore } from '../../store/chat';
import { useAuthStore } from '../../store/auth';

interface ModelInfo {
  id: string;
  name: string;
  description: string;
}

interface AvailableModels {
  [provider: string]: ModelInfo[];
}

interface ModelSelectorProps {
  workspace: Workspace;
  sessionId?: string;
  onModelChange?: (provider: string, model: string) => void;
}

export const ModelSelector: React.FC<ModelSelectorProps> = ({ 
  workspace, 
  sessionId,
  onModelChange 
}) => {
  const { currentSession, settings, updateSettings } = useChatStore();
  const { token, isAuthenticated, isLoading: authLoading } = useAuthStore();
  const [availableModels, setAvailableModels] = useState<AvailableModels>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingSwitch, setPendingSwitch] = useState<{provider: string, model: string} | null>(null);

  const currentProvider = (workspace.settings as any).preferredProvider || 'openai';
  const currentModel = (workspace.settings as any).preferredModel || 'gpt-4o-mini';

  // Load available models
  useEffect(() => {
    // Don't try to load models if auth is still loading or user is not authenticated
    if (authLoading || !isAuthenticated || !token) {
      return;
    }

    const loadAvailableModels = async () => {
      setIsLoading(true);
      setError(null);

      try {

        // Try to get all supported models first (fallback if workspace-specific fails)
        const response = await fetch(`${import.meta.env.VITE_API_URL}/api/models/supported`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error(`Failed to load models: ${response.statusText}`);
        }

        const data = await response.json();
        setAvailableModels(data.availableModels);
      } catch (err) {
        console.error('Failed to load available models:', err);
        setError(err instanceof Error ? err.message : 'Failed to load models');
      } finally {
        setIsLoading(false);
      }
    };

    loadAvailableModels();
  }, [token, isAuthenticated, authLoading]); // Wait for auth to complete

  // Create dropdown items
  const dropdownItems = React.useMemo(() => {
    const items: { id: string; text: string; provider: string; model: string; description: string }[] = [];
    
    Object.entries(availableModels).forEach(([provider, models]) => {
      models.forEach(model => {
        const isCurrentModel = provider === currentProvider && model.id === currentModel;
        items.push({
          id: `${provider}:${model.id}`,
          text: isCurrentModel ? `${model.name} (Current)` : model.name,
          provider,
          model: model.id,
          description: model.description
        });
      });
    });

    return items;
  }, [availableModels, currentProvider, currentModel]);

  const selectedItem = dropdownItems.find(item => 
    item.provider === currentProvider && item.model === currentModel
  );

  const handleModelSelect = (selectedValue: string) => {
    const selectedItem = dropdownItems.find(item => item.id === selectedValue);
    if (!selectedItem) return;

    const { provider, model } = selectedItem;
    
    // If it's the same model, do nothing
    if (provider === currentProvider && model === currentModel) {
      return;
    }

    // Check if there are messages to preserve
    const hasMessages = currentSession?.messages && currentSession.messages.length > 1; // More than just initial context
    
    if (hasMessages) {
      setPendingSwitch({ provider, model });
      setShowConfirmModal(true);
    } else {
      // No messages to preserve, switch immediately
      performModelSwitch(provider, model, false);
    }
  };

  const performModelSwitch = async (provider: string, model: string, preserveContext: boolean = true) => {
    setIsSwitching(true);
    setError(null);

    try {
      if (!token) {
        throw new Error('No authentication token');
      }

      const endpoint = sessionId 
        ? `/api/workspaces/${workspace.id}/sessions/${sessionId}/models/switch`
        : `/api/workspaces/${workspace.id}/models/switch`;

      const requestBody = {
        newProvider: provider,
        newModel: model,
        preserveContext,
        currentMessages: preserveContext ? currentSession?.messages : []
      };

      const response = await fetch(`${import.meta.env.VITE_API_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.details || `Failed to switch model: ${response.statusText}`);
      }

      const result = await response.json();

      // Update local settings to reflect the change
      const newWorkspaceSettings = {
        ...workspace.settings,
        preferredProvider: provider,
        preferredModel: model
      };

      // Update workspace object in place (this should trigger a re-render)
      (workspace.settings as any).preferredProvider = provider;
      (workspace.settings as any).preferredModel = model;

      // Update chat store settings
      updateSettings({
        provider,
        model
      });

      // Handle context restoration if available
      if (result.restoredContext) {
        console.log('Context restored from previous session with this model');
        // Could show a notification about restored context
      }

      // Notify parent component
      onModelChange?.(provider, model);

      console.log('Model switched successfully:', result);
    } catch (err) {
      console.error('Model switch failed:', err);
      setError(err instanceof Error ? err.message : 'Failed to switch model');
    } finally {
      setIsSwitching(false);
      setShowConfirmModal(false);
      setPendingSwitch(null);
    }
  };

  const handleConfirmSwitch = () => {
    if (pendingSwitch) {
      performModelSwitch(pendingSwitch.provider, pendingSwitch.model, true);
    }
  };

  const handleCancelSwitch = () => {
    setShowConfirmModal(false);
    setPendingSwitch(null);
  };

  // Show loading state if auth is loading or models are loading
  if (authLoading || isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <RobotOutlined style={{ fontSize: '16px' }} />
        <Skeleton.Input active size="small" style={{ width: 200 }} />
      </div>
    );
  }

  // Show a gentle message if not authenticated
  if (!isAuthenticated) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <RobotOutlined style={{ fontSize: '16px', opacity: 0.4 }} />
        <span style={{ 
          fontSize: '0.75rem',
          color: 'var(--color-text-tertiary)',
          fontStyle: 'italic'
        }}>
          Sign in to select models
        </span>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <WarningOutlined style={{ fontSize: '16px', color: 'var(--color-error)' }} />
        <span style={{ 
          fontSize: '0.75rem',
          color: 'var(--color-error)',
          fontFamily: 'monospace'
        }}>
          Model loading failed
        </span>
      </div>
    );
  }

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: '280px' }}>
        <RobotOutlined style={{ fontSize: '16px', opacity: 0.6 }} />
        {isSwitching ? (
          <Spin size="small" tip="Switching model..." />
        ) : (
          <Select
            value={selectedItem?.id}
            placeholder="Select model"
            options={dropdownItems.map(item => ({
              label: item.text,
              value: item.id
            }))}
            onChange={(value) => {
              if (value) {
                handleModelSelect(value);
              }
            }}
            size="small"
            style={{ 
              minWidth: '260px',
              backgroundColor: 'var(--color-bg-primary)',
              borderColor: 'var(--color-border-primary)',
              color: 'var(--color-text-primary)'
            }}
          />
        )}
      </div>

      {/* Confirmation Modal */}
      <Modal
        open={showConfirmModal}
        onCancel={handleCancelSwitch}
        title="Switch AI Model"
        onOk={handleConfirmSwitch}
        okText="Switch Model"
        cancelText="Cancel"
        width={400}
      >
        <p>
          You're about to switch from <strong>{currentProvider}/{currentModel}</strong> to{' '}
          <strong>{pendingSwitch?.provider}/{pendingSwitch?.model}</strong>.
        </p>
        <p>
          Your conversation history will be saved and can be restored if you switch back to the previous model.
        </p>
        <p>
          The new model will receive a summary of your current conversation to maintain context.
        </p>
      </Modal>

      {/* Error notification */}
      {error && (
        <Alert
          type="error"
          message="Model Switch Failed"
          description={error}
          closable
          onClose={() => setError(null)}
          style={{ marginTop: '0.5rem' }}
        />
      )}
    </>
  );
};